import { useCallback, useMemo, type ReactElement, type ReactNode } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type Omittable,
} from "@salestrak/ui";

import { useMemberItemResolver } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  useMemberSelection,
  useValueIds,
} from "#app/components/entity-selectors/use-member-selection";
import { useTeamSelection } from "#app/components/entity-selectors/use-team-selection";
import { UNASSIGNED_ASSIGNEE_ID } from "#app/lib/filters/build-where";
import { FILTER_MEMBER_STATUSES } from "#app/lib/role-utils";
import {
  directoryKind,
  OTHER_MEMBERS_GROUP,
  TEAM_MEMBERS_GROUP,
  type QuickFilterSection,
} from "#app/components/filters/quick-inputs/quick-filter-options";

const NO_ITEMS: EntityItemConfig[] = [];

type QuickEntityPickerProps = Omittable<{
  section: QuickFilterSection;
  value: unknown;
  onChange: (next: unknown) => void;
  /** A toolbar chip: the picker opens from it as a popover. Without one
   *  the picker renders inline, filling the menu or page that hosts it. */
  trigger?: ReactElement<Record<string, unknown>>;
}>;

const NO_IDS: string[] = [];

const UNASSIGNED_ITEM: EntityItemConfig = {
  id: UNASSIGNED_ASSIGNEE_ID,
  title: "Unassigned",
  avatar: { icon: "circle-dashed" },
};

/** Whether a quick filter picks members or teams — the sections whose
 *  values the entity pickers list instead of the plain option list. */
export function isEntityPickerSection(section: QuickFilterSection): boolean {
  return directoryKind(section.def.type) !== undefined;
}

function valueIds(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === "string")
    : NO_IDS;
}

/**
 * A member or team quick filter's values, as the same picker every member and
 * team selector opens: the windowed, server-searched pool with the selection
 * pinned above it. One component for every door — the toolbar Assignee chip,
 * the Simple panel's popup, the toolbar menu's submenu, the phone page.
 */
export function QuickEntityPicker(props: QuickEntityPickerProps) {
  return directoryKind(props.section.def.type) === "team" ? (
    <QuickTeamPicker {...props} />
  ) : (
    <QuickMemberPicker {...props} />
  );
}

function QuickMemberPicker({ section, value, onChange, trigger }: QuickEntityPickerProps) {
  const isAssignee = section.def.type === "entity-assignee";
  const { roster } = section;
  const ids = useValueIds(valueIds(value));
  const unassigned = ids.includes(UNASSIGNED_ASSIGNEE_ID);
  const memberIds = useMemo(
    () => ids.filter((id) => id !== UNASSIGNED_ASSIGNEE_ID),
    [ids],
  );
  const rosterIds = useMemo(
    () => (roster ? [...roster.memberIds] : undefined),
    [roster],
  );

  const { pool: memberPool, selections: memberSelections } = useMemberSelection({
    value: memberIds.filter((id) => !roster?.memberIds.has(id)),
    exclude: rosterIds,
    statuses: FILTER_MEMBER_STATUSES,
  });

  const itemFor = useMemberItemResolver();
  const rosterItems = useMemo(
    () =>
      (rosterIds ?? []).flatMap((id) => {
        const item = itemFor(id);
        return item && FILTER_MEMBER_STATUSES.includes(String(item.data?.memberStatus))
          ? [{ ...item, section: TEAM_MEMBERS_GROUP }]
          : [];
      }),
    [rosterIds, itemFor],
  );

  // ONE pool: a selector lists one pool at a time (several are tabs), so
  // Unassigned and the team's roster ride above the paged members as pinned
  // rows, where the search still reaches them. A pick past the loaded pages
  // is pinned too, under the other members' heading, which then heads the
  // paged list below it as well.
  const pickedPastPage = memberPool.pinnedItems ?? NO_ITEMS;
  const pool = useMemo<EntityPoolConfig>(
    () => ({
      ...memberPool,
      label: roster ? OTHER_MEMBERS_GROUP : memberPool.label,
      pinnedLabel: section.def.label,
      pinnedItems: [
        ...(isAssignee ? [UNASSIGNED_ITEM] : []),
        ...rosterItems,
        ...(roster
          ? pickedPastPage.map((item) => ({ ...item, section: OTHER_MEMBERS_GROUP }))
          : pickedPastPage),
      ],
    }),
    [memberPool, pickedPastPage, roster, isAssignee, rosterItems, section.def.label],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const picked: EntitySelection[] = [];
    if (unassigned) {
      picked.push({ id: UNASSIGNED_ASSIGNEE_ID, poolId: pool.id, entity: UNASSIGNED_ITEM });
    }
    for (const item of rosterItems) {
      if (memberIds.includes(item.id)) {
        picked.push({ id: item.id, poolId: pool.id, entity: item });
      }
    }
    return [...picked, ...memberSelections];
  }, [unassigned, rosterItems, memberIds, memberSelections, pool.id]);

  return (
    <QuickPicker
      pool={pool}
      showLabel={!!roster && pickedPastPage.length === 0}
      selections={selections}
      ids={ids}
      onChange={onChange}
      trigger={trigger}
      title={section.def.label}
      searchPlaceholder="Search members..."
      emptyMessage="No members found"
    />
  );
}

function QuickTeamPicker({ section, value, onChange, trigger }: QuickEntityPickerProps) {
  const ids = useValueIds(valueIds(value));
  const { pool, selections } = useTeamSelection({ value: ids });
  return (
    <QuickPicker
      pool={pool}
      selections={selections}
      ids={ids}
      onChange={onChange}
      trigger={trigger}
      title={section.def.label}
      searchPlaceholder="Search teams..."
      emptyMessage="No teams found"
    />
  );
}

function QuickPicker({
  pool,
  showLabel = false,
  selections,
  ids,
  onChange,
  trigger,
  title,
  searchPlaceholder,
  emptyMessage,
}: Omittable<{
  pool: EntityPoolConfig;
  /** Draw the pool's label — for a list split by pinned rows above it. */
  showLabel?: boolean;
  selections: EntitySelection[];
  ids: string[];
  onChange: (next: unknown) => void;
  trigger?: ReactElement<Record<string, unknown>>;
  title: string;
  searchPlaceholder: string;
  emptyMessage: string;
}>) {
  const config = useMemo<EntitySelectorConfig>(
    () => ({ pools: [pool], maxSelections: 0, searchPlaceholder, emptyMessage }),
    [pool, searchPlaceholder, emptyMessage],
  );

  // An applied id the picker cannot name yet (still being looked up) has no
  // row to toggle, so it rides along untouched instead of dropping out.
  const handleChange = useCallback(
    (next: EntitySelection[]) => {
      const shown = new Set(selections.map((selection) => selection.id));
      const kept = ids.filter((id) => !shown.has(id));
      const all = [...kept, ...next.map((selection) => selection.id)];
      onChange(all.length > 0 ? all : null);
    },
    [selections, ids, onChange],
  );

  const body: ReactNode = (
    <>
      <EntitySelectorInput />
      <EntitySelectorPool value={pool.id} hideLabel={!showLabel} />
      <EntitySelectorEmpty>{emptyMessage}</EntitySelectorEmpty>
    </>
  );

  if (trigger) {
    return (
      <EntitySelector config={config} value={selections} onValueChange={handleChange} title={title}>
        <EntitySelectorTrigger render={trigger} />
        <EntitySelectorContent align="end">{body}</EntitySelectorContent>
      </EntitySelector>
    );
  }
  return (
    <EntitySelector
      variant="inline"
      config={config}
      value={selections}
      onValueChange={handleChange}
      title={title}
    >
      {body}
    </EntitySelector>
  );
}
